// react
import { useState } from 'react';

// mui
import {
	alpha,
	Box,
	Button,
	Checkbox,
	Chip,
	CircularProgress,
	Collapse,
	FormControlLabel,
	Paper,
	Stack,
	Typography,
} from '@mui/material';

// icons
import {
	AutoAwesome as IconAI,
	CheckCircle as IconCheck,
	ExpandMore as IconExpandMore,
	ExpandLess as IconExpandLess,
} from '@mui/icons-material';

// services
import { generateConditions } from '../../../services/aiApi';
import type { AiConditionResult } from '../../../services/aiApi';
import { mapAiResultToFormValues } from '../../../services/conditionGenerationPrompt';

// types
import type { Condition, VerificationSection } from '../../../@types';

// components
import { AiDisclaimerBanner } from '../AiDisclaimerBanner/AiDisclaimerBanner';

// Props

interface AiConditionGeneratorProps {
	contractId: number;
	contractName: string;
	contractAmount: number;
	contractCurrency: string;
	existingConditions: Condition[];
	documentInsights: Array<{
		persona: string;
		summary: string;
		keyInformationTable?: Record<string, string>;
		checklist?: string[];
		disbursementConditions?: string;
		timelines?: string;
		responsibleParties?: string;
		keyRisks?: string;
	}> | null;
	insightsLoading?: boolean;
	/** Callback to add AI-generated sections to the condition's verification sections */
	onAddAiSections: (sections: VerificationSection[]) => void;
}

// Component

export const AiConditionGenerator = ({
	contractId,
	contractName,
	contractAmount,
	contractCurrency,
	existingConditions,
	documentInsights,
	insightsLoading,
	onAddAiSections,
}: AiConditionGeneratorProps) => {
	const [isGenerating, setIsGenerating] = useState(false);
	const [generatedConditions, setGeneratedConditions] = useState<AiConditionResult[]>([]);
	const [selectedIndices, setSelectedIndices] = useState<Set<number>>(new Set());
	const [error, setError] = useState<string | null>(null);
	const [expanded, setExpanded] = useState(true);
	const [applied, setApplied] = useState(false);

	const hasInsights = documentInsights && documentInsights.length > 0;

	const handleGenerate = async () => {
		setIsGenerating(true);
		setError(null);
		setGeneratedConditions([]);
		setSelectedIndices(new Set());
		setApplied(false);

		try {
			const response = await generateConditions(
				`Generate payment conditions based on the contract document for "${contractName}". Use the extracted payment terms, disbursement conditions, and milestones from the document insights.`,
				{
					contracts: [
						{
							id: contractId,
							name: contractName,
							amount: contractAmount,
							currency: contractCurrency,
						},
					],
					existingConditions: existingConditions.map((c) => ({
						name: c.name,
						paymentPercentage: c.paymentPercentage,
					})),
					documentInsights: documentInsights || undefined,
				}
			);

			if (!response.isMilestoneRelevant) {
				setError(response.reason || 'No payment milestones could be extracted from the documents.');
			} else {
				setGeneratedConditions(response.milestones);
				// Select all by default
				setSelectedIndices(new Set(response.milestones.map((_, i) => i)));
			}
		} catch (err) {
			setError(
				err instanceof Error ? err.message : 'Failed to generate conditions. Please try again.'
			);
		} finally {
			setIsGenerating(false);
		}
	};

	const handleToggleCondition = (idx: number) => {
		setSelectedIndices((prev) => {
			const next = new Set(prev);
			if (next.has(idx)) {
				next.delete(idx);
			} else {
				next.add(idx);
			}
			return next;
		});
	};

	const handleToggleAll = () => {
		if (selectedIndices.size === generatedConditions.length) {
			setSelectedIndices(new Set());
		} else {
			setSelectedIndices(new Set(generatedConditions.map((_, i) => i)));
		}
	};

	const handleAddSelected = () => {
		// Collect all verification sections from selected conditions into
		// a single "AI Generated Conditions" section group
		const aiSections: VerificationSection[] = [];

		for (const idx of selectedIndices) {
			const condition = generatedConditions[idx];
			const formValues = mapAiResultToFormValues(condition);
			if (formValues.verificationSections) {
				// Prefix each section name with the condition name for clarity
				for (const section of formValues.verificationSections) {
					aiSections.push({
						...section,
						name: `AI Generated Conditions — ${condition.name}: ${section.name}`,
					});
				}
			}
		}

		if (aiSections.length === 0) {
			// If selected conditions had no sections, create a placeholder section
			aiSections.push({
				id: crypto.randomUUID(),
				name: 'AI Generated Conditions',
				assignedTo: null,
				assignedGroupId: null,
				fields: [],
				status: 'PENDING' as const,
				verifiedAt: null,
				verifiedBy: null,
			});
		}

		onAddAiSections(aiSections);
		setApplied(true);
	};

	// Confidence badge color
	const getConfidenceBadge = (confidence: number) => {
		if (confidence >= 0.8) return { label: 'High', color: '#2e7d32', bg: '#e8f5e9' };
		if (confidence >= 0.5) return { label: 'Medium', color: '#e65100', bg: '#fff3e0' };
		return { label: 'Low', color: '#d32f2f', bg: '#ffebee' };
	};

	return (
		<Paper
			elevation={0}
			sx={{
				bgcolor: alpha('#7c3aed', 0.04),
				border: '1px solid',
				borderColor: alpha('#7c3aed', 0.15),
				borderRadius: 2,
				overflow: 'hidden',
			}}>
			{/* Header */}
			<Stack
				direction="row"
				alignItems="center"
				spacing={1}
				sx={{
					px: 2,
					py: 1.5,
					cursor: 'pointer',
					'&:hover': { bgcolor: alpha('#7c3aed', 0.06) },
				}}
				onClick={() => setExpanded(!expanded)}>
				<IconAI sx={{ fontSize: 20, color: '#7c3aed' }} />
				<Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#7c3aed', flex: 1 }}>
					Generate Conditions with AI
				</Typography>
				{expanded ? (
					<IconExpandLess sx={{ color: '#7c3aed' }} />
				) : (
					<IconExpandMore sx={{ color: '#7c3aed' }} />
				)}
			</Stack>

			<Collapse in={expanded}>
				<Box sx={{ px: 2, pb: 2, pt: 0 }}>
					<Stack spacing={2}>
						{/* AI disclaimer banner — first thing the user sees before
						    interacting with the AI surface. */}
						<AiDisclaimerBanner />

						{/* Description */}
						<Typography variant="body2" color="text.secondary">
							{insightsLoading
								? 'Loading document insights… Please wait for best results.'
								: hasInsights && documentInsights
									? `${documentInsights.length} AI insight${documentInsights.length !== 1 ? 's' : ''} available from contract documents. Click below to extract verification requirements automatically.`
									: 'No document insights available. Upload and process a contract document for better results.'}
						</Typography>

						{/* Generate button */}
						<Button
							variant="contained"
							startIcon={
								isGenerating || insightsLoading ? (
									<CircularProgress size={16} color="inherit" />
								) : (
									<IconAI />
								)
							}
							disabled={isGenerating || insightsLoading}
							onClick={handleGenerate}
							sx={{
								alignSelf: 'flex-start',
								textTransform: 'none',
								bgcolor: '#7c3aed',
								'&:hover': { bgcolor: '#6d28d9' },
							}}>
							{isGenerating ? 'Generating...' : 'Generate Conditions from Contract'}
						</Button>

						{/* Error */}
						{error && (
							<Typography variant="body2" color="error">
								{error}
							</Typography>
						)}

						{/* Applied confirmation */}
						{applied && (
							<Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 500 }}>
								<IconCheck sx={{ fontSize: 16, verticalAlign: 'text-bottom', mr: 0.5 }} />
								AI-generated condition sections added below.
							</Typography>
						)}

						{/* Generated condition cards */}
						{generatedConditions.length > 0 && !applied && (
							<Stack spacing={1.5}>
								<Stack direction="row" alignItems="center" justifyContent="space-between">
									<Typography variant="body2" sx={{ fontWeight: 600 }}>
										{generatedConditions.length} verification requirement
										{generatedConditions.length !== 1 ? 's' : ''} extracted
									</Typography>
									<FormControlLabel
										control={
											<Checkbox
												checked={selectedIndices.size === generatedConditions.length}
												indeterminate={
													selectedIndices.size > 0 &&
													selectedIndices.size < generatedConditions.length
												}
												onChange={handleToggleAll}
												size="small"
											/>
										}
										label={<Typography variant="caption">Select all</Typography>}
									/>
								</Stack>
								{generatedConditions.map((condition, idx) => {
									const badge = getConfidenceBadge(condition.confidence);
									const totalFields = condition.verificationSections.reduce(
										(sum, s) => sum + s.fields.length,
										0
									);
									const isSelected = selectedIndices.has(idx);
									return (
										<Paper
											key={idx}
											elevation={0}
											sx={{
												border: '1px solid',
												borderColor: isSelected ? '#7c3aed' : 'grey.200',
												borderRadius: 2,
												p: 2,
												bgcolor: isSelected ? alpha('#7c3aed', 0.03) : 'transparent',
												cursor: 'pointer',
												'&:hover': {
													borderColor: '#7c3aed',
													boxShadow: `0 0 0 1px ${alpha('#7c3aed', 0.2)}`,
												},
											}}
											onClick={() => handleToggleCondition(idx)}>
											<Stack spacing={1}>
												{/* Title row */}
												<Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
													<Checkbox
														checked={isSelected}
														size="small"
														sx={{
															p: 0,
															mr: 0.5,
															color: '#7c3aed',
															'&.Mui-checked': { color: '#7c3aed' },
														}}
														onClick={(e) => e.stopPropagation()}
														onChange={() => handleToggleCondition(idx)}
													/>
													<Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
														{condition.name}
													</Typography>
													<Chip
														label={badge.label}
														size="small"
														sx={{
															height: 20,
															bgcolor: badge.bg,
															color: badge.color,
															fontWeight: 600,
															'& .MuiChip-label': {
																px: 0.75,
																fontSize: '0.7rem',
															},
														}}
													/>
												</Stack>

												{/* Description */}
												{condition.description && (
													<Typography
														variant="body2"
														color="text.secondary"
														sx={{ fontSize: '0.85rem', pl: 3.5 }}>
														{condition.description}
													</Typography>
												)}

												{/* Sections summary */}
												<Typography variant="caption" color="text.secondary" sx={{ pl: 3.5 }}>
													{condition.verificationSections.length} section
													{condition.verificationSections.length !== 1 ? 's' : ''} &bull;{' '}
													{totalFields} field
													{totalFields !== 1 ? 's' : ''}
												</Typography>
											</Stack>
										</Paper>
									);
								})}

								{/* Add selected button */}
								<Button
									variant="contained"
									startIcon={<IconCheck />}
									disabled={selectedIndices.size === 0}
									onClick={handleAddSelected}
									sx={{
										alignSelf: 'flex-start',
										textTransform: 'none',
										bgcolor: '#7c3aed',
										'&:hover': { bgcolor: '#6d28d9' },
									}}>
									Add {selectedIndices.size} Selected as Verification Requirements
								</Button>
							</Stack>
						)}
					</Stack>
				</Box>
			</Collapse>
		</Paper>
	);
};
